import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { graphql, useStaticQuery } from "gatsby";
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Row from "react-bootstrap/Row";

import { SearchHit, useSearchQuery, useSuggestionsQuery } from "../../features/addSearchApiSlice";
import useClickOutside from "../../hooks/haiku/useClickOutside";
import useDebounce from "../../hooks/haiku/useDebounce";
import useEventListener from "../../hooks/haiku/useEventListener";
import { ReactComponent as IconClose18 } from "../../icons/icon/18/a-icon-close-18.svg";
import { ReactComponent as IconDeleteInput18 } from "../../icons/icon/18/a-icon-deleteinput-18.svg";
import { ReactComponent as IconSearch18 } from "../../icons/icon/18/a-icon-search-18.svg";
import { ReactComponent as IconWarning24 } from "../../icons/icon/24/a-icon-warning-24.svg";
import DataLayer from "../../utils/dataLayer";
import IconButton from "../button/iconButton";
import CategoryTag, { TagVariant } from "../categoryTag/categoryTag";
import Logo from "../logo/logo";
import SearchResult from "../searchResult/searchResult";
import SearchTerm from "../searchTerm/searchTerm";
import Spinner from "../spinner/spinner";

import "./search.scss";

const limit = 10;

interface SearchProps {
  onBlur?: () => void;
  onCancel?: () => void;
  onFocus?: () => void;
  desktop?: boolean | undefined;
}

interface staticQuerySearch {
  primaryNavigation: Queries.kontent_item_navigation_item;
  secondaryNavigation: Queries.kontent_item_navigation_item;
  settings: Queries.kontent_item_site_settings;
}

type SearchTermKey = "search_term__1" | "search_term__2" | "search_term__3" |
  "search_term__4" | "search_term__5" | "search_term__6";

const Search: React.FC<SearchProps> = ({
  onBlur = () => {},
  onCancel = () => {},
  onFocus = () => {},
  desktop = false,
}) => {
  const [page, setPage] = useState<number>(1);
  const [term, setTerm] = useState<string>("");
  const [searching, setSearching] = useState<boolean>(false);
  const [hasSearchInputFocus, setHasSearchInputFocus] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const intl = useIntl();

  const debouncedTerm = useDebounce(term, 300);

  const searchResult = useSearchQuery({
    limit,
    page,
    searchVariant: "classic",
    term: debouncedTerm,
  }, {
    skip: debouncedTerm.length < 3,
  });

  const suggestionsResult = useSuggestionsQuery({
    term: debouncedTerm,
  }, {
    skip: debouncedTerm.length < 3,
  });

  const loading = ((searchResult.isFetching || searchResult.isLoading || term !== debouncedTerm)
      && term.length >= 3 && page < 2)
    || term !== debouncedTerm;
  const first = searchResult.isUninitialized;
  const error = searchResult.isError;
  const noResult = searchResult.isSuccess && searchResult.status !== "pending" && searchResult.data.total_hits === 0 && term.length >= 3;

  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  const searchInnerRef = useRef<HTMLDivElement | null>(null);
  const searchHitsRef = useRef<HTMLDivElement | null>(null);

  const resetSearch = (resetTerm = true) => {
    if (resetTerm) {
      setTerm("");
    }

    if (desktop) {
      searchFieldRef?.current?.focus();
    } else {
      setSearching(false);
      setHasSearchInputFocus(false);
      onBlur();
    }
  };

  useClickOutside(searchInnerRef, (event) => {
    resetSearch(!(desktop || event?.target?.id === "search"));

    if (desktop) {
      event.preventDefault();
    }

    onBlur();
  });

  useEventListener("scroll", () => {
    if (
      searchInnerRef.current
      && searchHitsRef.current
      && searchInnerRef.current.scrollTop
      + searchInnerRef.current.offsetHeight
      + 100 > searchHitsRef.current.offsetHeight
    ) {
      if (searchResult.status === "fulfilled" && page < (searchResult.data?.total_hits || 0) / limit) {
        setPage(page + 1);
      }
    }
  }, searchInnerRef);

  useEffect(() => {
    if (desktop) {
      searchFieldRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (hasSearchInputFocus) {
      setShowSuggestions(true);

      return () => {
      };
    }

    const handler = setTimeout(() => {
      setShowSuggestions(false);
    }, 300);

    return () => clearTimeout(handler);
  }, [hasSearchInputFocus]);

  const data: staticQuerySearch = useStaticQuery(graphql`
    {
      primaryNavigation: kontentItemNavigationItem(system: {codename: {eq: "primary_navigation"}}) {
        elements {
          elements {
            value {
              ... on kontent_item_navigation_item {
                elements {
                  url_slug {
                    value
                  }
                }
              }
            }
          }
        }
      }
      secondaryNavigation: kontentItemNavigationItem(system: {codename: {eq: "secondary_navigation"}}) {
        elements {
          elements {
            value {
              ... on kontent_item_navigation_item {
                elements {
                  url_slug {
                    value
                  }
                }
              }
            }
          }
        }
      }
      settings: kontentItemSiteSettings(system: {codename: {eq: "site_settings"}}) {
        elements {
          search_term__1 {
            value
          }
          search_term__2 {
            value
          }
          search_term__3 {
            value
          }
          search_term__4 {
            value
          }
          search_term__5 {
            value
          }
          search_term__6 {
            value
          }
        }
      }
    }
  `);

  const searchSuggestions: JSX.Element[] = [];

  for (let i = 1; i <= 6; i += 1) {
    if (
      data.settings.elements
      && `search_term__${i}` in data.settings.elements
      && (data.settings.elements[`search_term__${i}` as SearchTermKey] as Queries.Maybe<Queries.kontent_item_text_element_value>)?.value
    ) {
      searchSuggestions.push((
        <SearchTerm
          key={data.settings.elements[`search_term__${i}`].value}
          onClick={() => {
            setTerm(data.settings?.elements?.[`search_term__${i}` as SearchTermKey]?.value || "");
            DataLayer.pushEvent("pp_search_suggestion", {
              dimension_search_variant: "classic",
              event_value: data.settings?.elements?.[`search_term__${i}` as SearchTermKey]?.value,
            });
          }}
        >
          {data.settings?.elements?.[`search_term__${i}` as SearchTermKey]?.value}
        </SearchTerm>
      ));
    }
  }

  const colors: Record<string, TagVariant> = {};

  data.primaryNavigation.elements?.elements?.value?.forEach(
    (item: Queries.Maybe<Queries.kontent_item>, index: number) => {
      switch (index) {
        case 0:
          colors[(item as Queries.kontent_item_navigation_item)?.elements?.url_slug?.value || "primary-0"] = "primary";
          break;
        case 1:
        case 2:
          colors[(item as Queries.kontent_item_navigation_item)?.elements?.url_slug?.value || "primary-2"] = "tertiary";
          break;
        default:
          break;
      }
    },
  );

  data.secondaryNavigation.elements?.elements?.value?.forEach(
    (item: Queries.Maybe<Queries.kontent_item>, index: number) => {
      switch (index) {
        case 0:
          colors[(item as Queries.kontent_item_navigation_item)?.elements?.url_slug?.value || "secondary-0"] = "tertiary";
          break;
        default:
          break;
      }
    },
  );

  return (
    <Container
      fluid
      className={classNames("search", { "flex-fill search--searching": searching })}
      ref={!desktop ? searchInnerRef : null}
    >
      <Row className="search__top">
        {desktop && (
          <Col lg={3}>
            <a
              href="/"
              aria-label={intl.formatMessage({ id: "navigation.logo_title" })}
              title={intl.formatMessage({ id: "navigation.logo_title" })}
            >
              <Logo breakpoint="lg" />
            </a>
          </Col>
        )}
        <Col lg={6} className="search__input">
          <InputGroup className="prepend">
            <Form.Control
              placeholder={intl.formatMessage({ id: "search.type" })}
              onChange={(event) => {
                setPage(1);
                setTerm(event.target.value);
              }}
              onFocus={() => {
                onFocus();
                setSearching(true);
                setHasSearchInputFocus(true);
              }}
              onBlur={() => {
                setHasSearchInputFocus(false);
              }}
              ref={searchFieldRef}
              value={term}
              id="search"
            />
            <InputGroup.Prepend>
              <InputGroup.Text>
                <IconSearch18 />
              </InputGroup.Text>
            </InputGroup.Prepend>
            {searching && term && (
              <button
                type="button"
                className="btn-clean"
                onClick={() => {
                  resetSearch();
                }}
                aria-label={intl.formatMessage({ id: "button.clear_search.aria_label" })}
              >
                <IconDeleteInput18 className="text-black" />
              </button>
            )}
          </InputGroup>
        </Col>
        {desktop && (
          <Col md={{ offset: 1, span: 2 }} className="text-right">
            <IconButton
              onClick={() => {
                onCancel();

                resetSearch();
              }}
              className="btn-utility"
              iconOnly
              aria-label={intl.formatMessage({ id: "button.close_search.aria_label" })}
            >
              <IconClose18 />
            </IconButton>
          </Col>
        )}
      </Row>
      <div
        className="search__inner"
        ref={desktop ? searchInnerRef : null}
      >
        {searching && (
          <>
            {
              loading
                ? (
                  <Row className="search__info-text search__info-text--passive">
                    <Col xs={12} lg={{ offset: 3, span: 6 }} className="justify-content-center d-flex">
                      <Spinner size="lg" className="text-grey-dark" />
                    </Col>
                  </Row>
                )
                : (
                  <>
                    {
                      error || noResult
                        ? (
                          <>
                            {error && (
                              <Row className="search__info-text">
                                <Col xs={12} lg={{ offset: 3, span: 6 }}>
                                  <IconWarning24 /><br />
                                  <FormattedMessage id="search.error" />
                                </Col>
                              </Row>
                            )}
                            {noResult && (
                              <Row className="search__info-text">
                                <Col xs={12} lg={{ offset: 3, span: 6 }}>
                                  <FormattedMessage
                                    id="search.noresult"
                                    values={{
                                      term,
                                    }}
                                  />
                                </Col>
                              </Row>
                            )}
                          </>
                        )
                        : (
                          <>
                            {first && searchSuggestions.length > 0 && (
                              <>
                                <small className="search__result-count">
                                  <FormattedMessage id="search.popular" />
                                </small>
                                <div
                                  className="search-suggestions"
                                >
                                  {searchSuggestions}
                                </div>
                              </>
                            )}
                            {(((searchResult.isSuccess && searchResult.data.hits.length > 0) || page > 1) && term.length >= 3) && (
                              <>
                                {showSuggestions && (suggestionsResult.data?.suggestions?.length ?? 0) > 0 && term.length > 3 && (
                                  <>
                                    <div
                                      className="search-suggestions"
                                    >
                                      {suggestionsResult.data?.suggestions.map(({ value }) => (
                                        <SearchTerm
                                          popular
                                          key={value}
                                          onClick={() => {
                                            setTerm(value);
                                            DataLayer.pushEvent(
                                              "pp_search_suggestion",
                                              {
                                                dimension_search_variant: "classic",
                                                event_value: value,
                                              },
                                            );
                                          }}
                                        >
                                          {value}
                                        </SearchTerm>
                                      ))}
                                    </div>
                                  </>
                                )}
                                <small className="search__result-count">
                                  <FormattedMessage id="search.results" /> ({searchResult.data?.total_hits})
                                </small>
                                <div
                                  className="search-results"
                                  ref={searchHitsRef}
                                >
                                  {searchResult.data?.hits.map((hit: SearchHit, index: number) => {
                                    const urlPaths = hit.categories?.slice(1).map((item: string) => ({
                                      link: "#",
                                      title: item.replace(/[0-9]+x/, ""),
                                    }));
                                    let tag = null;

                                    if (urlPaths[0] && hit.custom_fields?.customTag) {
                                      tag = (
                                        <CategoryTag variant={colors[urlPaths[0].title]} small>
                                          {hit.custom_fields.customTag}
                                        </CategoryTag>
                                      );
                                    }

                                    return (
                                      <SearchResult
                                        category={tag}
                                        documentType={hit.document_type}
                                        id={hit.id}
                                        imageBase64={hit.images.main_b64}
                                        imageSrc={hit.images.main}
                                        key={hit.id}
                                        keyword={debouncedTerm}
                                        position={index + 1}
                                        text={hit.highlight}
                                        title={typeof hit.title === "string" ? hit.title.replace("| DKMS", "") : ""}
                                        url={hit.url}
                                      />
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </>
                        )
                    }
                  </>
                )
            }
          </>
        )}
      </div>
    </Container>
  );
};

export default Search;
